/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import {
  ComposableCard,
  type GDSComposableCardBodyContentProps,
  type GDSComposableCardCtaSectionProps,
  type GDSComposableCardBackgroundColor,
  useClickableCard,
  GDSColorToken,
  GDSProgressBarProps,
} from '@leagueplatform/genesis-core';
import {
  MasonryDeeplinkAction,
  MasonryDismissAction,
  MasonryDriverAction,
} from '#types/masonry-driver-node-actions.ts';
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import { Link } from '@leagueplatform/routing';
import { isExternalUrl } from '@leagueplatform/web-common';
import { isLinkAction } from '#utils/is-link-action.ts';
import { formatClassNames } from '#utils/format-masonry-class-name.util.ts';
import { resolveBackgroundColorValue } from '#utils/resolve-background-color-value.util.ts';
import { mapIconTintToken } from '#utils/map-icon-tint-token.ts';
import { useInvokeAction } from '../../hooks/use-invoke-action.hook';
import { useInvokeActionWithLoadingState } from '../../hooks/use-invoke-action-with-loading-state.hook';

type BodyProps = {
  backgroundImage?: string;
  backgroundOverlay?: boolean;
  imageWidth?: string;
  imageHeight?: string;
  tags?: Array<
    Omit<
      NonNullable<GDSComposableCardBodyContentProps['tags']>[number],
      'iconTint'
    > & {
      iconTint?: GDSColorToken;
    }
  >;
} & GDSComposableCardBodyContentProps;

export type MasonryComposableCardNodeProperties = {
  backgroundColor?: GDSComposableCardBackgroundColor;
  dismissButtonLabel?: string;
  media?: {
    mediaSrc?: string;
    mediaAltText?: string;
    mediaHeight?: string;
  };
  body?: BodyProps;
  cta?: GDSComposableCardCtaSectionProps;
  progressBar?: GDSProgressBarProps;
};

export type MasonryComposableCardNodeActions = {
  onDismiss?: MasonryDismissAction;
  onFirstButtonClick?: MasonryDriverAction;
  onSecondButtonClick?: MasonryDriverAction;
  onCardClick?: MasonryDriverAction;
};

export type MasonryComposableCardNode = MasonryEngineNode<
  'composableCard',
  MasonryComposableCardNodeProperties,
  MasonryComposableCardNodeActions,
  {
    contentSlot?: MasonryEngineNode<string, any>[];
  }
>;

export type MasonryComposableCardRendererProps =
  MasonryEngineNodeRendererProps<MasonryComposableCardNode>;

const getActionUrl = (
  action: MasonryDriverAction | undefined,
): string | undefined => {
  if (action && isLinkAction(action)) {
    return (action as MasonryDeeplinkAction).payload.url;
  }
  return undefined;
};

export const MasonryComposableCardRenderer = ({
  backgroundColor = 'primary',
  dismissButtonLabel,
  media,
  body,
  progressBar,
  cta,
  onDismiss,
  onFirstButtonClick,
  onSecondButtonClick,
  onCardClick,
  contentSlot,
  locatorId,
  className,
  engineClassName,
  css,
  rootElementRef,
}: MasonryComposableCardRendererProps) => {
  const { invokeAction: invokeFirstCtaAction, isLoading: isFirstCtaInFlight } =
    useInvokeActionWithLoadingState();
  const {
    invokeAction: invokeSecondCtaAction,
    isLoading: isSecondCtaInFlight,
  } = useInvokeActionWithLoadingState();
  const invokeAction = useInvokeAction();

  const {
    backgroundImage,
    backgroundOverlay,
    imageWidth,
    imageHeight,
    tags,
    ...bodyProps
  } = body || {};
  const { position, firstCTA, secondCTA } = cta || {};

  const { onCardMouseDown, onCardMouseUp, primaryActionRef, clickAreaRef } =
    useClickableCard(rootElementRef);

  const bodyHref = getActionUrl(onCardClick);
  const firstButtonHref = getActionUrl(onFirstButtonClick);
  const secondButtonHref = getActionUrl(onSecondButtonClick);

  const tagsWithMappedTintToken = tags?.map(mapIconTintToken);

  const updatedBodyProps = {
    ...bodyProps,
    tags: tagsWithMappedTintToken,
  };

  return (
    <ComposableCard.Root
      data-testid={locatorId}
      backgroundColor={resolveBackgroundColorValue(backgroundColor)}
      backgroundImage={backgroundImage}
      backgroundOverlay={backgroundOverlay}
      dismissButtonLabel={dismissButtonLabel}
      className={formatClassNames(engineClassName, className)}
      css={css}
      ref={clickAreaRef}
      {...(onDismiss && {
        onDismiss: (event: React.MouseEvent) => {
          if (onDismiss) {
            invokeAction({
              event,
              ...onDismiss,
            });
          }
        },
      })}
      {...media}
      {...(onCardClick && {
        onCardMouseDown,
        onCardMouseUp,
      })}
    >
      {body && (
        <ComposableCard.Body
          {...updatedBodyProps}
          css={{
            ...updatedBodyProps.css,
            ...(imageWidth || imageHeight
              ? {
                  '.GDS-composable-card-body-image': {
                    ...(imageWidth && { width: imageWidth }),
                    ...(imageHeight && { height: imageHeight }),
                  },
                }
              : {}),
          }}
          {...(onCardClick && {
            ref: primaryActionRef,
            href: bodyHref,
            linkAs: !isExternalUrl(bodyHref) ? Link : undefined,
            to: !isExternalUrl(bodyHref) ? bodyHref : undefined,
            onClick: (event: React.MouseEvent) => {
              if (onCardClick) {
                invokeAction({
                  event,
                  ...onCardClick,
                });
              }
            },
          })}
        />
      )}
      {progressBar && <ComposableCard.ProgressBar progressBar={progressBar} />}
      {contentSlot && (
        <ComposableCard.Content>{contentSlot}</ComposableCard.Content>
      )}
      {cta && (
        <ComposableCard.CTASection
          position={position}
          firstCTA={{
            onClick: (event: React.MouseEvent) => {
              if (onFirstButtonClick) {
                invokeFirstCtaAction({
                  event,
                  ...onFirstButtonClick,
                });
              }
            },
            href: firstButtonHref,
            as: !isExternalUrl(firstButtonHref) ? Link : undefined,
            to: !isExternalUrl(firstButtonHref) ? firstButtonHref : undefined,
            ...firstCTA,
            // if the cta is not a url, we need to show the loading and disabled states
            ...(!firstButtonHref
              ? {
                  loading: firstCTA?.loading || isFirstCtaInFlight,
                  disabled: firstCTA?.disabled || isFirstCtaInFlight,
                }
              : {}),
          }}
          secondCTA={{
            onClick: (event: React.MouseEvent) => {
              if (onSecondButtonClick) {
                invokeSecondCtaAction({
                  event,
                  ...onSecondButtonClick,
                });
              }
            },
            href: secondButtonHref,
            as: !isExternalUrl(secondButtonHref) ? Link : undefined,
            to: !isExternalUrl(secondButtonHref) ? secondButtonHref : undefined,
            ...secondCTA,
            // if the cta is not a url, we need to show the loading and disabled states
            ...(!secondButtonHref
              ? {
                  loading: secondCTA?.loading || isSecondCtaInFlight,
                  disabled: secondCTA?.disabled || isSecondCtaInFlight,
                }
              : {}),
          }}
        />
      )}
    </ComposableCard.Root>
  );
};
